/**
 * useLanguage
 *
 * Custom hook for managing language settings across authenticated and unauthenticated pages
 *
 * Primary responsibilities:
 * - Provide consistent language handling across the application
 * - Sync language between localStorage and user profile when authenticated
 * - Provide language detection and switching functionality
 *
 * Dependencies:
 * - i18next for internationalization
 * - Supabase for authenticated user profile storage
 */

import { useEffect, useState, useContext } from 'react'
import i18n from '@/i18n'
import { ChatbotUIContext } from '@/context/context'
import { supabase } from '@/lib/supabase/browser-client'

// Helper function to save language to localStorage
const saveLanguageToLocalStorage = (language: string | null) => {
  if (typeof window !== 'undefined') {
    if (language) {
      localStorage.setItem('userLanguage', language)
    } else {
      localStorage.removeItem('userLanguage')
    }
  }
}

// Helper function to get language from localStorage
const getLanguageFromLocalStorage = (): string | null => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('userLanguage')
  }
  return null
}

// Helper function to find best match for browser language
const findBestMatch = (browserLang: string): string | null => {
  // Convert to lowercase for consistent matching
  const langCode = browserLang.toLowerCase()

  // Direct match with supported languages
  const supportedLangs = i18n.options.supportedLngs
  if (supportedLangs && Array.isArray(supportedLangs) && supportedLangs.includes(langCode)) {
    return langCode
  }

  // Try to match language part only (e.g., 'en-US' -> 'en')
  const baseLang = langCode.split('-')[0]
  if (supportedLangs && Array.isArray(supportedLangs) && supportedLangs.includes(baseLang)) {
    return baseLang
  }

  // Special case handling for specific languages
  if (langCode.startsWith('zh')) {
    if (langCode.includes('tw') || langCode.includes('hk')) {
      return 'zh-TW' // Traditional Chinese
    } else {
      return 'zh-CN' // Simplified Chinese
    }
  }

  if (langCode.startsWith('pt')) {
    if (langCode.includes('br')) {
      return 'pt-BR' // Brazilian Portuguese
    } else {
      return 'pt-PT' // European Portuguese
    }
  }

  if (langCode.startsWith('es')) {
    if (
      langCode.includes('419') ||
      langCode.includes('mx') ||
      langCode.includes('ar') ||
      langCode.includes('cl') ||
      langCode.includes('co') ||
      langCode.includes('cr') ||
      langCode.includes('do') ||
      langCode.includes('ec') ||
      langCode.includes('gt') ||
      langCode.includes('hn') ||
      langCode.includes('ni') ||
      langCode.includes('pa') ||
      langCode.includes('pe') ||
      langCode.includes('pr') ||
      langCode.includes('sv') ||
      langCode.includes('uy') ||
      langCode.includes('ve')
    ) {
      return 'es-419' // Latin American Spanish
    } else {
      return 'es-ES' // European Spanish
    }
  }

  return null
}

export function useLanguage() {
  const { profile, setProfile } = useContext(ChatbotUIContext)
  const [currentLanguage, setCurrentLanguage] = useState<string>('en')
  const [isAutoDetect, setIsAutoDetect] = useState(true)
  const [isLoading, setIsLoading] = useState(true)

  // Initialize language on component mount
  useEffect(() => {
    const initializeLanguage = async () => {
      setIsLoading(true)
      const browserLang = navigator.language || (navigator as any).userLanguage
      const storedLanguage = getLanguageFromLocalStorage()

      try {
        // Case 1: Profile has a default language
        if (profile?.default_language) {
          setIsAutoDetect(false)
          setCurrentLanguage(profile.default_language)
          await i18n.changeLanguage(profile.default_language)
          saveLanguageToLocalStorage(profile.default_language)
          console.log(`Using profile default language: ${profile.default_language}`)
        }
        // Case 2: No profile language but localStorage has a value
        else if (storedLanguage) {
          setIsAutoDetect(false)
          setCurrentLanguage(storedLanguage)
          await i18n.changeLanguage(storedLanguage)
          console.log(`Using stored language from localStorage: ${storedLanguage}`)

          // Update profile if authenticated
          if (profile?.id) {
            await supabase
              .from('profiles')
              .update({ default_language: storedLanguage })
              .eq('id', profile.id)

            if (setProfile) {
              setProfile((prev) => (prev ? { ...prev, default_language: storedLanguage } : prev))
            }
          }
        }
        // Case 3: Auto-detect (no stored preferences)
        else {
          const bestMatch = findBestMatch(browserLang) || 'en'
          setIsAutoDetect(true)
          setCurrentLanguage(bestMatch)
          await i18n.changeLanguage(bestMatch)
          // Clear any stored language to ensure auto-detect works
          saveLanguageToLocalStorage(null)
          console.log(`Auto-detected language: ${browserLang} -> ${bestMatch}`)
        }
      } catch (error) {
        console.error('Error initializing language:', error)
        // Fallback to English
        setCurrentLanguage('en')
        await i18n.changeLanguage('en')
      } finally {
        setIsLoading(false)
      }
    }

    initializeLanguage()
  }, [profile?.id, profile?.default_language, setProfile])

  // Function to change language
  const changeLanguage = async (locale: string) => {
    try {
      if (locale === 'auto') {
        // Set to auto-detect mode
        const browserLang = navigator.language || (navigator as any).userLanguage
        const bestMatch = findBestMatch(browserLang) || 'en'

        // Clear stored preferences
        saveLanguageToLocalStorage(null)
        setIsAutoDetect(true)
        setCurrentLanguage(bestMatch)
        await i18n.changeLanguage(bestMatch)

        // Update profile if authenticated
        if (profile?.id) {
          await supabase.from('profiles').update({ default_language: null }).eq('id', profile.id)

          if (setProfile) {
            setProfile((prev) => (prev ? { ...prev, default_language: null } : prev))
          }
        }

        console.log(`Auto-detected and changed to ${bestMatch}`)
      } else {
        // Set to specific language
        setIsAutoDetect(false)
        setCurrentLanguage(locale)
        await i18n.changeLanguage(locale)

        // Save to localStorage
        saveLanguageToLocalStorage(locale)

        // Update profile if authenticated
        if (profile?.id) {
          await supabase.from('profiles').update({ default_language: locale }).eq('id', profile.id)

          if (setProfile) {
            setProfile((prev) => (prev ? { ...prev, default_language: locale } : prev))
          }
        }

        console.log(`Changed language to ${locale}`)
      }
    } catch (error) {
      console.error('Error changing language:', error)
    }
  }

  return {
    currentLanguage,
    isAutoDetect,
    isLoading,
    changeLanguage
  }
}
